import React from "react";
import classNames from "classnames";
import moment from "moment";
import DateRangePicker, { DateRange } from "../_shared/DateRangePicker";
import styles from "./style.module.scss";

const disp = (s: string) => moment(s, "YYYY-MM-DD").format("DD/MM/YYYY");

interface DashboardControlsProps {
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  activeCount: number;
  onCustomize: () => void;
  /** direct PDF export of the current dashboard view */
  onExport: () => void;
  /** disable Export until every widget has finished loading */
  exportDisabled?: boolean;
}

/** Date-range picker + Customize Widgets + Export controls row. */
export default function DashboardControls({
  range,
  onRangeChange,
  activeCount,
  onCustomize,
  onExport,
  exportDisabled = false,
}: DashboardControlsProps) {
  return (
    <div className={styles.controls}>
      {/* The interactive picker renders blank in the html2canvas PDF capture, so
          it's excluded (ho-no-print) and overlaid on a plain static pill that
          IS captured. The static pill is the in-flow element (it sizes the slot,
          so the slot never collapses when the live picker is removed); the live
          picker is absolutely overlaid on top and covers it on screen. */}
      <div className={styles.dateSlot}>
        <div className={styles.pickerStatic} aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9A469B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="17" rx="2" />
            <path d="M3 9h18M8 2v4M16 2v4" />
          </svg>
          <span className={styles.staticLabel}>
            {disp(range.from)} - {disp(range.to)}
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#868E96" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
        <div className={classNames(styles.pickerLive, "ho-no-print")}>
          <DateRangePicker value={range} onChange={onRangeChange} />
        </div>
      </div>

      {/* ho-no-print: interactive buttons are chrome — kept out of the PDF */}
      <button
        type="button"
        className={classNames(styles.customizeBtn, "ho-no-print")}
        onClick={onCustomize}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6h10M4 12h16M4 18h7" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="11" cy="18" r="2" />
        </svg>
        <span className={styles.customizeLabel}>Customize Widgets</span>
        <span className={styles.count}>{activeCount}</span>
      </button>

      <button
        type="button"
        className={classNames(styles.reportBtn, "ho-no-print")}
        onClick={onExport}
        disabled={exportDisabled}
        title={
          exportDisabled ? "Please wait — the dashboard is still loading" : undefined
        }
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <path d="M7 10l5 5 5-5" />
          <path d="M12 15V3" />
        </svg>
        <span className={styles.reportLabel}>Export</span>
      </button>
    </div>
  );
}
